import React, { ReactElement } from 'react';

import { Inject } from '@odinjs/odin';

import { FetchAPI, FieldAPI, FieldListValue, Stamp, StampIcon } from '@philips-emr/tws-components';
import { Color } from '@philips-emr/tws-constants';
import { Value } from '@philips-emr/tws-core';
import Controller, { OnBeforeFetch, OnClick, OnDynamicContentRender, OnMount, OnSelectItem, OnUnselectItem } from '@philips-emr/tws-core/lib/client';
import { SubmittableFragmentContext } from '@philips-emr/tws-core/lib/component/context';
import { MountEvent, RenderEvent, SelectItemEvent, SubmitEvent } from '@philips-emr/tws-core/lib/component/types';
import { msg } from '@philips-emr/tws-core/lib/services';

import BaseController from '../BaseController';

import { BeneficiarioSelecionado, IeStatusTrabalhista } from './base-exclusao-beneficiario-controller';

import './locator-beneficiarios-exclusao-controller.scss';

type DataValue = Record<string, Value>;

@Controller({ fragment: 'locator-beneficiarios-exclusao' })
export default class LocatorBeneficiariosExclusaoController extends BaseController {

  @Inject('SubmittableFragmentContext')
  private submittableFragmentContext!: SubmittableFragmentContext;

  private beneficiariosSelecionados: FieldListValue = [];

  @OnMount({ target: 'field-titular' })
  public onMountFieldTitular(event: MountEvent): void {
    const field = event.target as unknown as FieldAPI;
    const value = field.getData() as unknown as BeneficiarioSelecionado;
    value && value.nrSeqSegurado && field.setLabel(value.dsTitularidade);
  }

  @OnSelectItem({ target: 'deck-beneficiarios' })
  public onSelectItemBeneficiarios(event: SelectItemEvent<DataValue>): void {
    const canAdd = this.beneficiariosSelecionados.filter(beneficiario => this.checkBeneficiarioEqual(beneficiario, event)).length == 0;

    if (canAdd) {
      this.beneficiariosSelecionados.push(event.item);
    }

  }

  @OnUnselectItem({ target: 'deck-beneficiarios' })
  public onUnselectItemBeneficiarios(event: SelectItemEvent<DataValue>): void {
    const index = this.beneficiariosSelecionados.findIndex(beneficiario => this.checkBeneficiarioEqual(beneficiario, event));

    if (index > -1) {
      this.beneficiariosSelecionados.splice(index, 1);
    }

  }

  @OnBeforeFetch({ target: 'fetch-beneficiarios' })
  public onBeforeFetchBeneficiarios(event: MountEvent): void {
    const fetch = event.target as unknown as FetchAPI;

    const ids = this.context.get('beneficiarios') as number[] || [];
    const nrSeqSubestipulante = this.context.get('nrSeqSubestipulante') as number;
    const beneficiarios = { beneficiarios: ids.join(',') };
    fetch.setParameters({ checkDataRescisao: true, ...beneficiarios, ...fetch.getParameters(), ...(nrSeqSubestipulante && { nrSeqSubestipulante }) });
  }

  @OnClick({ target: 'button-confirmar' })
  public onClickButtonConfirmar(): void {
    this.submittableFragmentContext ?.submit(new SubmitEvent({ record: this.beneficiariosSelecionados }));
  }

  @OnDynamicContentRender({ target: 'hpms-custom-card-beneficiarios' })
  public onDynamicContentRender(event: RenderEvent): void {
    const { data, render } = event;
    const { ieSituacaoTrabalhista } = data as BeneficiarioSelecionado;

    const content = (): ReactElement => this.getBeneficiarioLoteStampByStatus(ieSituacaoTrabalhista);
    render(content);
  }

  private getBeneficiarioLoteStampByStatus(ieStatus: IeStatusTrabalhista): ReactElement {
    return (
      <Stamp
        className="c-locator-beneficiarios-exclusao--custom-stamp-margin"
        color={this.getColorStatusTrabalhista(ieStatus)}
        icon={this.getIconStatusTrabalhista(ieStatus)}
        label={this.getLabelStatusTrabalhista(ieStatus)}
      />
    );
  }

  private getLabelStatusTrabalhista(ieStatus: IeStatusTrabalhista): string {
    const label = {
      [IeStatusTrabalhista.S]: 'stipulator.situacao-trabalhista/s',
      [IeStatusTrabalhista.N]: 'stipulator.situacao-trabalhista/n',
    };

    return label[ieStatus] && msg(label[ieStatus]);
  }

  private getIconStatusTrabalhista(ieStatus: IeStatusTrabalhista): StampIcon {
    const icon = {
      [IeStatusTrabalhista.S]: StampIcon.WARNING,
      [IeStatusTrabalhista.N]: StampIcon.CHECK,
    };

    return icon[ieStatus];
  }

  private getColorStatusTrabalhista(ieStatus: IeStatusTrabalhista): Color {
    const color = {
      [IeStatusTrabalhista.S]: Color.SAFETY_ORANGE,
      [IeStatusTrabalhista.N]: Color.SAFETY_NEUTRAL,
    };

    return color[ieStatus];
  }

  private checkBeneficiarioEqual = (beneficiario: DataValue, event: SelectItemEvent<DataValue>): boolean =>
    beneficiario.nrSeqSegurado == event.item.nrSeqSegurado;

}
